import baseSlugify from '@sindresorhus/slugify';
import { LatLng } from 'leaflet';
import { IArea, IStateAwareArea } from 'types/map';
import getImagePath from 'utils/getImagePath';
import { LODGINGS_AREA_ID, MIN_ZOOM_LEVEL_FOR_DESTINATIONS } from 'components/Map/constants';

export { default as getMinimumZoomThatFits } from './getMinimumZoomThatFits';
export { default as parseImageName } from './parseImageName';

export const NON_MAP_LOCATION_IDS = [
  2, // Your Lodgings
  15, // a dark place
];

export function createAreaTooltipButtons(
  area: IStateAwareArea,
  currentArea: IArea | undefined,
  onAreaClick?: (_: any, area: IArea) => void,
) {

  // We can't travel if we don't know how to travel,
  // or if the area's locked
  if (!onAreaClick || !(area.unlocked || area.gateEvent)) {
    return [];
  }

  // We can't travel to where we already are
  if (area.id === currentArea?.id) {
    return [];
  }

  const action = (e: any) => onAreaClick(e, area);
  const label = area.shouldShowGateIcon ? 'Go' : 'Travel';

  return [{ label, action }];
}

export function areaToTooltipData(
  area: IStateAwareArea,
  currentArea: IArea | undefined,
  onAreaClick?: (_: any, area: IArea) => void,
  showImage: boolean = true
) {
  const {
    description,
    name,
    image,
    unlocked,
    unavailableDescription,
  } = area;

  const smallButtons = createAreaTooltipButtons(area, currentArea, onAreaClick);

  return {
    name,
    smallButtons,
    description: unlocked ? description : unavailableDescription,
    image: showImage ? image : undefined,
    imagePath: showImage ? getImagePath({ icon: image, type: 'small-icon' }) : undefined,
  };
}

export const xy = (x: number, y: number) => new LatLng(y, x);

export const findParentArea = (area: IArea, areas: IArea[]) => {
  return areas.find(parent => {
    return !!(parent.childAreas || []).find(child => child.areaKey === area.areaKey);
  });
};

export function getHitboxForArea(area: IArea, zoomLevel: number) {
  if (isDistrict(area) && zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
    return area.mainDestinationHitbox!;
  }
  return area.hitbox!;
  //if (isDistrict(area) && zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS) {
    //return area.hitbox;
  //}
}

export const hasHitbox = (area: IArea) => (area.hitbox?.length ?? 0) > 0;

export const isCurrentArea = (area: IArea, currentArea: IArea | undefined) => area.areaKey === currentArea?.areaKey;

export const isDestination = (a?: IArea) => a?.type === 'Destination';

export const isDistrict = (a?: IArea) => (a?.type ?? null) === 'District';

export const isDrawable = (a: IArea) => (a.spriteTopLeftX !== undefined && a.spriteTopLeftY !== undefined);

// Can the user do something with this area?
export const isInteractable = (a: IArea) => a.canMoveTo && (a.unlocked || !!a.gateEvent);

export const isInteractableAtThisZoomLevel = (a: IArea, zoomLevel: number) => !isDistrict(a) || zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS;

export const isLandmark = (a?: IArea) => (a?.type ?? null) === 'Landmark';

export const isLabelled = (a: IArea) => (a.labelX ?? -1) >= 0 && (a.labelY ?? 1) <= 0;

export function isLit(area: IArea) {
  // A discovered or unlocked area is lit, always
  if (area.discovered || area.unlocked) {
    return true;
  }

  // An undiscovered area is lit if any of its children are discovered
  if (isDistrict(area)) {
    const { childAreas } = area;
    return childAreas
        ?.some(otherArea => !isLandmark(otherArea) && (otherArea.discovered || otherArea.unlocked))
      ?? false;
  }

  // Otherwise it's undiscovered
  return false;
}

export const isMapArea = (a: IArea) => !NON_MAP_LOCATION_IDS.includes(a.id);

export const isMappable = (area: IArea, areas: IArea[]) => {
  // Lodgings and sublodgings aren't drawn on the map
  if (isLodgings(area) || isSubLodgings(area, areas)) {
    return false;
  }
  // Validate coordinates
  return !((area.labelX ?? Number.MIN_SAFE_INTEGER) < 0 || (area.labelY ?? Number.MAX_SAFE_INTEGER) > 0);

};

export const isLodgings = (a: IArea) => a.id === LODGINGS_AREA_ID;

export const isSubLodgings = (area: IArea, areas: IArea[]) => {
  const lodgings = (areas || []).find(isLodgings);
  if (!lodgings) {
    return false;
  }
  return !!lodgings.childAreas.find(childArea => childArea.id === area.id);
};

export const isSelectable = (a?: IArea) => (a?.unlocked ?? false) || (a?.discovered ?? false);

// TODO(sdob): This needs to return false when a child area of the district has been discovered
export const isUndiscoveredDistrict = (a: IArea) => isDistrict(a) && !a.discovered;

export const isUnlockable: (_: IArea) => boolean = (a: IArea) => !!a.gateEvent;

export const shouldAppearOnMap = (a: IArea) => (a.labelX ?? Number.MIN_SAFE_INTEGER) > 0
  && (a.labelY ?? Number.MAX_SAFE_INTEGER) < 0;

// Districts always have hitboxes (either for the whole district, or for their main destination);
// destinations only have hitboxes above the zoom threshold
export const shouldHaveHitbox = (area: IArea, zoomLevel: number) => {
  return isDistrict(area) || zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS;
};

export const shouldShowGateIcon = (area: IArea) => !area.unlocked && isUnlockable(area);

export const shouldShowLockIcon = (area: IArea) => !area.unlocked && !isUnlockable(area);

export const shouldShowBorderFanciness = (area: IArea, zoomLevel: number) => {
  if (isLandmark(area)) {
    return false;
  }
  if (isDistrict(area)) {
    return true;
  }
  return zoomLevel >= MIN_ZOOM_LEVEL_FOR_DESTINATIONS;
};

export const shouldShowMainDestination = (area: IArea) => isDistrict(area) && area.unlocked;

export const shouldShowTooltip = (area: IArea) => {
  return area.type !== 'Landmark';
};

export const shouldZoomOnTapAtZoomLevel = (area: IArea, zoomLevel: number) => {
  if (!isDistrict(area)) { // Only districts zoom in
    return false;
  }
  // Otherwise, are we already zoomed in?
  return zoomLevel < MIN_ZOOM_LEVEL_FOR_DESTINATIONS;
};

export const slugify = (name: string) => baseSlugify(
  name
    .replace(/^[Tt]he /, '')
    .replace(/'/, ''),
  {},
);

export const sortByLayer = (a: IArea, b: IArea) => a.zIndex - b.zIndex;

export const sortByLayerWithDestinationsLast = (a: IArea, b: IArea) => {
  if (isDistrict(a) && !isDistrict(b)) {
    return -1;
  }
  if (isDistrict(b) && !isDistrict(a)) {
    return 1;
  }
  return sortByLayer(a, b);
};
